<template>
  <ul class="pagination">
    <li class="pagination-item">
      <button
        class="paginate-buttons prev"
        type="button"
        @click="onClickPreviousPage"
        :disabled="isInFirstPage"
        aria-label="Go to previous page"
      >
        Prev
      </button>
    </li>

    <li
      v-for="(page, index) in pages"
      class="pagination-item page-number"
      :key="index"
    >
      <button
        v-if="page.name != 0"
        type="button"
        @click="onClickPage(page.name)"
        :disabled="page.isDisabled"
        :class="{ visited: isPageActive(page.name) }"
        :aria-label="`Go to page number ${page.name}`"
      >
        {{ page.name }}
      </button>
    </li>

    <li class="pagination-item">
      <button
        class="paginate-buttons next"
        type="button"
        @click="onClickNextPage"
        :disabled="isInLastPage"
        aria-label="Go to next page"
      >
        Next
      </button>
    </li>
  </ul>
</template>

<script>
export default {
  props: {
    maxVisibleButtons: {
      type: Number,
      required: false,
      default: 3
    },
    totalPages: {
      type: Number,
      default: null
    },
    total: {
      type: Number
    },
    perPage: {
      type: Number,
      default: 10
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pagechanged: {
      type: Function,
      default: () => {}
    }
  },
  computed: {
    startPage() {
      if (this.currentPage === 1) {
        return 1;
      }
      if (this.currentPage === this.totalPages) {
        return this.totalPages - this.maxVisibleButtons + 1;
      }
      return this.currentPage - 1;
    },
    endPage() {
      return Math.min(
        this.startPage + this.maxVisibleButtons - 1,
        this.totalPages
      );
    },
    pages() {
      const range = [];
      for (let i = this.startPage; i <= this.endPage; i += 1) {
        range.push({
          name: i,
          isDisabled: i === this.currentPage
        });
      }
      return range;
    },
    isInFirstPage() {
      return this.currentPage === 1;
    },
    isInLastPage() {
      return this.currentPage === this.totalPages;
    }
  },
  methods: {
    onClickPreviousPage() {
      this.currentPage = this.currentPage - 1;

      this.pagechanged(this.currentPage);
    },
    onClickPage(page) {
      this.pagechanged(page);
    },
    onClickNextPage() {
      this.currentPage = this.currentPage + 1;
      this.pagechanged(this.currentPage);
    },
    isPageActive(page) {
      return this.currentPage === page;
    }
  }
};
</script>

<style scoped>
.pagination .paginate {
  display: flex;
  align-items: center;
}
.pagination {
  list-style-type: none;
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 0;
  padding: 0;
}

.pagination-item {
  display: inline-block;
}

.visited {
  background: var(--grad-accent);
  color: var(--surface);
  border-color: transparent;
}

.paginate-buttons {
  padding: 6px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  background: var(--surface);
  font-weight: 600;
  font-size: var(--fs-sm);
  line-height: 1.4;
  letter-spacing: 0;
  color: var(--text);
  cursor: pointer;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
}

.paginate-buttons:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.paginate-buttons:not(:disabled):hover {
  background: var(--surface-2);
}

.next {
  border-radius: var(--radius-sm);
}
.prev {
  border-radius: var(--radius-sm);
}

.page-number button {
  padding: 6px 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  background: var(--surface);
  font-size: var(--fs-sm);
  font-weight: 500;
  color: var(--text);
  cursor: pointer;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
}

.page-number button:not(:disabled):not(.visited):hover {
  background: var(--surface-2);
}
</style>
